// Pure payment-status badge logic — no React/Firebase, so it's testable and
// shared between the customer list and the dashboard. DISPLAY ONLY: it never
// changes an invoice; it just decides what the badge reads.

// The customer's CURRENT billing-cycle start = the most recent 1st (or 15th
// for 15th-cycle customers) on or before `now`. The badge reflects only
// invoices from this boundary forward, so it RESETS automatically when a new
// cycle begins — a prior month's paid invoice stops reading "Paid".
export function currentCycleStart(customer, now = new Date()) {
  const y = now.getFullYear(), mo = now.getMonth(), d = now.getDate()
  const iso = (yy, mm, dd) => `${yy}-${String(mm + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  if (String(customer?.billingCycle || '').toLowerCase() === '15th') {
    if (d >= 15) return iso(y, mo, 15)
    const pm = mo === 0 ? 11 : mo - 1
    const py = mo === 0 ? y - 1 : y
    return iso(py, pm, 15)
  }
  return iso(y, mo, 1) // '1st' / custom / unknown → first of the current month
}

// The NEXT cycle start = one month after the current cycle start, on the same
// cycle day (1st or 15th). Used as the exclusive upper bound of the current cycle.
export function nextCycleStart(customer, now = new Date()) {
  const [y, m, d] = currentCycleStart(customer, now).split('-').map(Number)
  const ny = m === 12 ? y + 1 : y
  const nm = m === 12 ? 1 : m + 1
  return `${ny}-${String(nm).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

// The customer's current-cycle invoice: the invoice that BELONGS to this cycle,
// i.e. its dueDate falls in the window [cycleStart, nextCycleStart). Bounded on
// BOTH ends so an already-generated future/next-month invoice can never drive the
// current row's badge (which would mislabel a paid current cycle as unpaid).
// Returns null when none exists yet (invoice not generated) — which the badge
// treats as Unpaid. `invoices` may be an array or an object keyed by id.
export function currentCycleInvoice(customer, invoices, now = new Date()) {
  const start = currentCycleStart(customer, now)
  const next = nextCycleStart(customer, now)
  let best = null
  for (const inv of Object.values(invoices || {})) {
    if (!inv || !inv.dueDate || inv.dueDate < start || inv.dueDate >= next) continue
    if (!best
      || inv.dueDate > best.dueDate
      || (inv.dueDate === best.dueDate && (inv.createdAt || 0) > (best.createdAt || 0))) best = inv
  }
  return best
}

// Badge state for the current-cycle invoice:
//   'paid'       → invoice.status === 'paid' (set on payment_intent.succeeded,
//                  i.e. money actually SETTLED — cards instantly, ACH after 1-3d)
//   'processing' → charged and clearing (status 'processing', or 'pending' with
//                  a PaymentIntent already created)
//   'unpaid'     → nothing charged yet, a failed charge, or no current invoice
export function paymentBadgeState(inv) {
  const s = String(inv?.status || '').toLowerCase()
  if (s === 'paid') return 'paid'
  if (s === 'processing') return 'processing'
  if (s === 'pending' && inv?.stripePaymentIntentId) return 'processing'
  return 'unpaid'
}
